import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/context/ModalContext";
import { capitalizeFirstWord } from "@/lib/utils";
import {
  getProgramRoles,
  createOrUpdateProgramRole,
  getPermissionGroups,
  getPortfolios,
  deleteProgramRole,
  updateProgramRole,
  getNonCustomerProgramRoles,
  getProgramCustomerRoles,
  getProgramRoleAccessPolicies,
  createRoleAccessPolicy,
  deleteRoleAccessPolicy,
  RoleAccessMode,
  createProgramRoleV2,
} from "@/api/program-steps";

/**
 * GET /role/program/{program_id}
 * Retrieve roles for a specific program
 */
export function useGetProgramRoles(programId: string) {
  return useQuery({
    queryKey: ["program-roles", programId],
    queryFn: () => getProgramRoles(programId),
    enabled: !!programId,
  });
}

export function useGetProgramRoleAccessPolicies(programId: string, enabled = true) {
  return useQuery({
    queryKey: ["program-role-access-policies", programId],
    queryFn: () => getProgramRoleAccessPolicies(programId),
    enabled: !!programId && enabled,
    retry: false,
  });
}

export const useCreateRoleAccessPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { program: string; parent: string; child: string; mode: RoleAccessMode }) => createRoleAccessPolicy(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: ["program-role-access-policies", payload.program],
      });
      queryClient.invalidateQueries({
        queryKey: ["program-roles", payload.program],
      });
    },
  });
};

export const useDeleteRoleAccessPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId }: { policyId: string; programId: string }) => deleteRoleAccessPolicy(policyId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({
        queryKey: ["program-role-access-policies", programId],
      });
      queryClient.invalidateQueries({
        queryKey: ["program-roles", programId],
      });
    },
  });
};

export function useGetNonCustomerProgramRoles(programId: string) {
  return useQuery({
    queryKey: ["non-customer-program-roles", programId],
    queryFn: () => getNonCustomerProgramRoles(programId),
    enabled: !!programId,
  });
}

export function useGetProgramCustomerRoles(programId: string) {
  return useQuery({
    queryKey: ["program-customer-roles", programId],
    queryFn: () => getProgramCustomerRoles(programId),
    enabled: !!programId,
  });
}

/**
 * POST /role/program/{program_id}
 * Create or update a role for a specific program
 */
export const useCreateOrUpdateProgramRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, payload }: { programId: string; payload: object }) => createOrUpdateProgramRole(programId, payload),
    onSuccess: (_, { programId }) => {
      // Invalidate the roles query so UI refreshes
      queryClient.invalidateQueries({ queryKey: ["program-roles", programId] });
    },
  });
};

export const useCreateProgramRoleV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, payload }: { programId: string; payload: object }) => createProgramRoleV2(programId, payload),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ["program-roles", programId] });
    },
  });
};

/**
 * GET /role/permission-group
 * Retrieve all permission groups
 */
export function useGetPermissionGroups() {
  return useQuery({
    queryKey: ["permission-groups"],
    queryFn: () => getPermissionGroups(),
  });
}

/**
 * GET /role/portfolio
 * Retrieve all portfolios
 */
export function useGetPortfolios() {
  return useQuery({
    queryKey: ["portfolios"],
    queryFn: () => getPortfolios(),
  });
}

/**
 * DELETE /role/{role_id}
 * Delete a role for a specific program
 */
export const useDeleteProgramRole = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: ({ roleId }: { roleId: string; programId: string }) => deleteProgramRole(roleId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ["program-roles"] });
      queryClient.invalidateQueries({ queryKey: ["program-roles", programId] });
      showModal("done", {
        title: "Deleted",
        description: "The role was deleted successfully.",
        onConfirm: hideModal,
      });
    },
    onError: (error: unknown) => {
      const errorRecord =
        error && typeof error === "object"
          ? (error as Record<string, unknown>)
          : {};
      const rawMessage = errorRecord.error ?? errorRecord.message;
      const description =
        typeof rawMessage === "string" && rawMessage
          ? capitalizeFirstWord(rawMessage)
          : "There was an error deleting the role.";
      showModal("error-message", {
        title: "Error",
        description,
      });
    },
  });
};

/**
 * PUT /role/program/{program_id}/{role_id}
 * Update a role for a specific program
 */
export const useUpdateProgramRole = () => {
  return useMutation({
    mutationFn: ({ programId, roleId, payload }: { programId: string; roleId: string; payload: any }) =>
      updateProgramRole({ programId, roleId, payload }),
  });
};
